import { useCallback, useEffect, useRef } from 'react'

import { scrollToHighlightedCalendarGroup } from '../utils/scrollToHighlightedCalendarGroup'

type HighlightScrollOptions = Parameters<typeof scrollToHighlightedCalendarGroup>[0]

/**
 * Owns the highlighted calendar row scroll so that at most one runs at a time and it never outlives the calendar view.
 * It is stopped when a new one starts, when the page unmounts and when `calendarVisible` turns false, e.g. the user
 * switches from Game Calendars to Feed or Event Stats. Starting it while `calendarVisible` is false is allowed: a
 * highlight made from Feed or Event Stats starts it just before the page navigates to the calendar.
 */
export const useHighlightScroll = (calendarVisible: boolean) => {
  const stopRef = useRef<() => void>()

  const stop = useCallback(() => {
    stopRef.current?.()
    stopRef.current = undefined
  }, [])

  const start = useCallback(
    (options: HighlightScrollOptions) => {
      stop()
      stopRef.current = scrollToHighlightedCalendarGroup(options)
    },
    [stop]
  )

  useEffect(() => {
    if (!calendarVisible) stop()
  }, [calendarVisible, stop])

  useEffect(() => stop, [stop])

  return { start, stop }
}
